"use client";

import { useMemo } from "react";
import { LayoutDashboard } from "lucide-react";
import { useTranslations } from "next-intl";

import { EmptyState } from "@/components/shared/empty-state";

import { useDashboard } from "../hooks/use-dashboard";
import { useDashboardRealtime } from "../hooks/use-dashboard-realtime";
import { DashboardCustomizeSheet } from "./dashboard-customize-sheet";
import { DashboardGrid, DashboardGridItem } from "./dashboard-grid";
import { DashboardSkeleton } from "./dashboard-skeleton";
import { DashboardWidget } from "./dashboard-widget";

import type { WidgetDefinition } from "@/types";

export function Dashboard() {
  const t = useTranslations("Dashboard");
  const { data, isLoading: isCatalogLoading } = useDashboard();

  // The widgets to render, in the order the backend already reconciled.
  const visible = useMemo<WidgetDefinition[]>(() => {
    if (!data) return [];
    const byKey = new Map(data.widgets.map((w) => [w.key, w]));
    return data.visible
      .map((key) => byKey.get(key))
      .filter((w): w is WidgetDefinition => !!w);
  }, [data]);

  const params = data?.layout.params ?? {};

  // Push-driven, per card: a widget's declared triggers refresh only that card.
  // The dashboard is excluded from the app-wide invalidation sweep in
  // use-system-messages-realtime so the two don't both fire.
  useDashboardRealtime(visible);

  // The catalog is the only thing the page as a whole waits on — every card
  // fetches independently after it, and shows its own skeleton while it does.
  if (isCatalogLoading) {
    return <DashboardSkeleton />;
  }

  // The customize sheet is offered whenever there is anything to customize —
  // including when every widget is currently hidden, which is the only way back.
  const customizer = data && data.widgets.length > 0 && (
    <div className="mb-4 flex justify-end">
      <DashboardCustomizeSheet
        widgets={data.widgets}
        layout={data.layout}
        slotLimits={data.slot_limits}
      />
    </div>
  );

  const header = visible.filter((w) => w.slot === "header");
  const grid = visible.filter((w) => w.slot !== "header");

  // Four columns, stepping 1 → 2 → 4 as in shadcn's dashboard-01. The strip is
  // meant to read as a single line of figures, which is why the slot is capped
  // at four.
  const headerStrip = header.length > 0 && (
    <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {header.map((definition) => (
        <DashboardWidget
          key={definition.key}
          definition={definition}
          params={params[definition.key]}
          compact
        />
      ))}
    </div>
  );

  if (visible.length === 0) {
    // Reachable in normal use: a partner holds few permissions, so the catalog
    // can legitimately filter down to nothing — and a user may also have hidden
    // everything.
    return (
      <>
        {customizer}
        <EmptyState
          icon={LayoutDashboard}
          title={t("empty")}
          description={t("emptyHint")}
        />
      </>
    );
  }

  return (
    <>
      {customizer}
      {headerStrip}
      <DashboardGrid>
        {grid.map((definition) => (
          <DashboardGridItem key={definition.key} size={definition.size}>
            <DashboardWidget
              definition={definition}
              params={params[definition.key]}
            />
          </DashboardGridItem>
        ))}
      </DashboardGrid>
    </>
  );
}
